/**
 * Handles the filters and templates for the content loader
 * 
 * @exports
 * @class MaterialsViewrr
 */
import { FadeSlideUp } from '../../animations/fade-slide-up';
import { get, getAll } from '../../helpers/utilities';
import { ContentLoader } from '../../modules/content-loader';

import { materialTemplate, materialTypeTemplate } from './material-templates';

export class MaterialsViewer {
    private root: Element;
    private uiElements: MaterialsUI;
    private animationInstance!: FadeSlideUp;


    /**
     * Initialises a new instance of MaterialsViewer
     */
    constructor() {
        this.root = get('#js-materials-content') as Element;
        
        // Store our UI elements
        this.uiElements = {
            loadMore: get('#js-materials__load-more') as Element,
            disableElements: getAll('.js-materials__ui-elem') as Element[],
            clearFilterBtns: getAll('.js-materials__clear-filters') as Element[],
            filters: getAll('.js-materials__filter') as HTMLInputElement[],
        }

        // Get the animation instance for our elements
        const materialsList = get('#js-materials__list') as Element;
        if(materialsList) this.animationInstance = FadeSlideUp.get_instance(materialsList);

        // Initialise our content viewer
        this.initLoader();
        this.bindEvents();
    }


    /**
     * Binds the events for this class
     * 
     * @private
     * @returns void
     */
    private bindEvents(): void {

        // Scroll links
        const scrollLinks = getAll('.js-materials__scroll');
        if(scrollLinks) {
            scrollLinks.forEach(link => {
                link.addEventListener('click', () => {
                    window.smoothScroll.animateScroll(get('#js-materials__filters-block') as Element);
                });
            });
        }

        // Clear the filters on click
        this.uiElements.clearFilterBtns.forEach(filterBtn => {
            filterBtn.addEventListener('click', (event) => {
                this.uiElements.filters.forEach(filter => {
                    filter.choices ? filter.choices.setChoiceByValue('') : filter.value = '';
                });
                event.preventDefault();
            });
        });

        // Toggle the visibility of the filters on mobile
        const filtersBlock = get('#js-materials__filters');
        const filtersBtn   = get('#js-materials__filter-btn');
        if(filtersBlock && filtersBtn) {
            filtersBtn.addEventListener('click', () => {
                filtersBlock.classList.toggle('c-materials__filters--is-hidden');
            });
        }

        // Update the Material select box value when clicking on a material type box
        const materialFilter = get('#js-materials__type-filter');
        if(materialFilter) {
            this.root.addEventListener('click', (event) => {
                const target = (event.target as HTMLElement).classList.contains('js-materials__update-type') ? event.target as HTMLElement : (event.target as HTMLElement).closest('.js-materials__update-type');
                
                if(target) {
                    const value = target.getAttribute('data-value');
                    if(value) materialFilter.choices.setChoiceByValue(value);
                }
            });
        }
    }


    /**
     * Initialises our content loader
     * 
     * @private
     * @returns void
     */
    private initLoader(): void {
        const insertPoint  = get('#js-materials__insert-point');
        if(this.root && insertPoint) {

            new ContentLoader(this.root, {
                renderer: (data: ContentLoaderJSONResponse) => this.materialRenderer(data as MaterialsJsonResponse),
                toggleUI: (shouldDisable: boolean) => { this.toggleUI(shouldDisable) },
                itemSelector: '.js-materials__item',
                events: {
                    afterInsert: (elements: Element[]) => { this.displayNewMaterials(elements); }
                },
                insertOptions: {
                    type: 'before',
                    element: insertPoint
                },
            });
        }
    }


    /**
     * Renders the materials and passes then back to the content loader
     * 
     * @param data The JSON data object.
     * @param loader The loader instance.
     * @returns An array of HTML strings
     */
    private materialRenderer(data: MaterialsJsonResponse): string[] {
        const materialStrings: string[] = [];

        data.items.forEach(material => {
            
            if(material.type === 'material') {
                materialStrings.push(materialTemplate((material as unknown) as MaterialsItem));
            } else {
                materialStrings.push(materialTypeTemplate((material as unknown) as MaterialsTypeItem));
            }
        });

        return materialStrings;
    }


    /**
     * Toggle the UI disabled or enabled
     * 
     * @private
     * @returns void
     */
    private toggleUI(shouldDisable: boolean): void {

        if(shouldDisable) {
            this.uiElements.disableElements.forEach(uiElement => {
                uiElement.classList.add('c-materials__ui--is-disabled');
            });
        } else {
            this.uiElements.disableElements.forEach(uiElement => {
                uiElement.classList.remove('c-materials__ui--is-disabled');
            });
        }
    }


    /**
     * Displays our new elements
     * 
     * @param elements The elements to display
     */
    private displayNewMaterials(elements: Element[]): void {
        elements.push(this.uiElements.loadMore);

        // Animate the new elements in
        this.animationInstance.add(elements);
    }
}
